import { useT } from "../../contexts/LanguageContext";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { searchUsers, startChatWith } from "../../api/operator";

const UserSearchModal = ({ onClose, onChatStarted }) => {
  const t = useT();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const doSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 1) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await searchUsers(term.trim());
      setResults(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(q), 250);
    return () => clearTimeout(debounceRef.current);
  }, [q, doSearch]);

  const handleStart = async (user) => {
    setStarting(user.id);
    try {
      const res = await startChatWith(user.id);
      onChatStarted?.(res.chat_id, user);
      onClose();
    } catch (e) {
      alert(`Start chat failed: ${e.message}`);
    } finally {
      setStarting(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-20">
      <div className="dark-card rounded-xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-700">
          <MagnifyingGlassIcon className="w-5 h-5 text-text-secondary" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("search.placeholder")}
            className="flex-1 bg-transparent text-white outline-none placeholder-text-secondary"
          />
          <button onClick={onClose} className="text-text-secondary hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {error && (
            <div className="p-4 text-red-400 text-sm">{error}</div>
          )}
          {loading && results.length === 0 && (
            <div className="p-6 text-center text-text-secondary text-sm">{t("search.searching")}</div>
          )}
          {!loading && q && results.length === 0 && !error && (
            <div className="p-6 text-center text-text-secondary text-sm">
              Юзер табылмады
            </div>
          )}
          {!q && (
            <div className="p-6 text-center text-text-secondary text-sm">
              Аты, телефон не email енгіз
            </div>
          )}
          {results.map((u) => (
            <button
              key={u.id}
              onClick={() => handleStart(u)}
              disabled={starting === u.id}
              className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-bg-primary transition-colors disabled:opacity-50"
            >
              <div className="w-10 h-10 rounded-full bg-accent-grad flex items-center justify-center text-white font-bold">
                {u.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate">
                  {u.name || "—"}{" "}
                  <span className="text-xs text-text-secondary">#{u.id}</span>
                </div>
                <div className="text-xs text-text-secondary truncate">
                  {u.phone_number || u.email || "—"}
                  {u.user_type ? ` · ${u.user_type}` : ""}
                </div>
              </div>
              <span className="text-xs text-blue-400">
                {starting === u.id ? "..." : `${t("search.startChat")} →`}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserSearchModal;
